import {useAnalytics} from '@/ts/composables/stateful/use-analytics';
import {
  clickProperties,
  commonProperties,
  contentProperties,
  journalProperties,
  renderedTiles,
  tileProperties,
  type TodayClick,
  TodayEvent,
  type TodayTileReport,
} from '@/ts/composables/pure/use-today-events';
import {resolveTodayGreeting} from '@/ts/composables/pure/use-today-greeting';
import type {TodayContent, TodayRow, TodayTileKind} from '@/ts/types/component/today-page';
import {useI18n} from 'vue-i18n';

/** Sends the Today page's own events. One method per event, called from where it happens. */
export interface TodayAnalytics {
  pageViewed(): void;
  tileViewed(kind: TodayTileKind, report: TodayTileReport): void;
  tileClicked(kind: TodayTileKind, report: TodayTileReport, click: TodayClick): void;
  contentShown(kind: TodayTileKind, content: TodayContent): void;
  introWatched(contentId: string): void;
  journalSaved(entry: {hasText: boolean; feelingsCount: number}): void;
}

/**
 * The one place the Today page talks to analytics.
 *
 * The rows arrive as a getter rather than an argument on every method: they are read when an event
 * fires, which is what lets a tile report the page it was actually seen on, and no caller can pass
 * a stale set by accident.
 *
 * Every event carries `platform` and `locale` (IPD-2974 8.3). The third property that section asks
 * for, `journey_state`, is not sent: it names one of seven whole-page states that exist in the
 * epic's own document and nowhere in the code — the backend resolves each tile's state on its own
 * and the payload carries no global one, so there is nothing to fill it with. What the QA check
 * behind it actually compares is a tile's state against what the matrix allows in that row, and
 * that is `tile_state` on the tile events. Adding the page-level property later is additive and
 * does not orphan anything measured before it.
 */
export function useTodayAnalytics(rows: () => TodayRow[]): TodayAnalytics {
  const {trackEvent} = useAnalytics();
  // Through vue-i18n the way every other composable reads the language, rather than the instance
  // module: that one imports the locale files the build generates, so anything reaching it cannot
  // be loaded by a unit test — which is why only the entry points and the router import it.
  const {locale} = useI18n();
  // Once per visit, and deliberately not in a store the way `use-watched-report` keeps its own
  // mark: that one means once per session, so it has to outlive a mount. This means once per view
  // of the page, so it has to die with it.
  let pageReported = false;

  /**
   * The rest of the once-per-view marks, and they are HERE rather than beside what raises them
   * because this composable is the only thing on the page that survives a re-fetch.
   *
   * `loading-error` draws the page under a `v-else`, so `load()` unmounts the whole bento and
   * builds it again — and `finishQuestionnaire` calls `load()`, which makes it an ordinary flow
   * rather than an edge. Everything inside that subtree comes back with empty state: the grid's
   * in-view tracker forgets which cells it reported, and the video player forgets it reported.
   * Left there, the tile events counted a second time against a visit that is deliberately counted
   * once, which is the ratio the whole ticket exists to measure. This page's own setup is not
   * unmounted, so a mark kept here lasts exactly one view — which is what "once per view" meant.
   *
   * Content is keyed by cell AND piece: a re-fetch can legitimately put a different piece in the
   * same cell, and that is a new thing offered, while the same piece coming back is not.
   */
  const viewedTiles = new Set<TodayTileKind>();
  const shownContent = new Set<string>();
  const watchedIntro = new Set<string>();

  /* eslint-disable @typescript-eslint/naming-convention -- property names are the wire format, the
     same snake_case the backend's own events use. Renaming them here renames them in PostHog. */
  const common = (): Record<string, unknown> => commonProperties(locale.value);

  return {
    /**
     * The visit, reported once the page has something to report: the tile list is the answer to
     * "what was this user given", and at mount there is no payload yet and so no tiles.
     *
     * A visit that never gets its payload raises nothing, which is the honest denominator — a page
     * that failed to load was not shown. That also makes this event the one to divide the tile
     * events by, which is why it must not count someone the page is about to redirect. It differs
     * from the backend page-view call next to it on purpose: that one continues a series the old
     * home page started and has to stay unconditional to keep the two comparable, while this one
     * starts its own and has no history to protect.
     *
     * Not gated on the tab being in front, though the dwell behind the tile events is: a
     * background tab did load and draw the page, and the automatic page view PostHog sends counts
     * it too. Gating this one alone would leave the denominator measuring something other than the
     * rest of the tool.
     */
    pageViewed(): void {
      if (pageReported) {
        return;
      }
      pageReported = true;
      trackEvent(TodayEvent.pageViewed, {
        ...common(),
        // The same bands as the greeting the user is reading (04:00, 12:00 and 17:00), from the
        // same function, so the event and the screen can never disagree about the time of day.
        time_of_day: resolveTodayGreeting(new Date()),
        tiles_rendered: renderedTiles(rows()),
      });
    },

    /**
     * A tile that reached the screen and stayed there, which is the question engineering raised in
     * the doc review: whether the lower tiles are lost to scrolling.
     *
     * Guarded here rather than left to the primitive that calls it: that one reports each ELEMENT
     * once, which stops being once per view the moment a re-fetch replaces the elements.
     *
     * `tile_state` is the property that carries what 8.3 wanted `journey_state` for: a state the
     * matrix can be checked against. It is the face the tile was drawing at the moment it was seen,
     * not at the moment it was drawn — the two differ for a tile whose payload settles late.
     */
    tileViewed(kind: TodayTileKind, report: TodayTileReport): void {
      if (viewedTiles.has(kind)) {
        return;
      }
      viewedTiles.add(kind);
      trackEvent(TodayEvent.tileViewed, {
        ...common(),
        ...tileProperties(rows(), kind, report),
      });
    },

    /**
     * A control inside a tile was pressed, which is the question about whether the page sends
     * anyone anywhere.
     *
     * Reported on activation, navigation included, so a card that is a link counts the same as one
     * that opens a modal. A modifier-click counts too: it fires an ordinary `click` — the router
     * declines to navigate afterwards but the listener has already run — and it does take the user
     * to the destination, in a tab of their own. What escapes is the middle-click, which raises
     * only `auxclick`. That bias is small and constant, and listening for `auxclick` as well would
     * also catch the right-click that opens a context menu and goes nowhere.
     *
     * The state travels with it for the same reason it travels with a view: what a tile was
     * drawing is what the overlap matrix can be checked against.
     */
    tileClicked(kind: TodayTileKind, report: TodayTileReport, click: TodayClick): void {
      trackEvent(TodayEvent.tileClicked, {
        ...common(),
        ...tileProperties(rows(), kind, report),
        ...clickProperties(click),
      });
    },

    /**
     * A piece of reading was put in front of the user, which with the click above gives the rate
     * at which what the page offers gets opened.
     *
     * Sent when the cell holding it comes on screen rather than when the page draws it: the two
     * content cells sit below the fold on a phone, so reporting them as shown at load would count
     * as offered what nobody scrolled to. It rides on the same moment as the tile being seen, and
     * carries the same once-per-view mark — by cell and piece together, so a re-fetch that puts
     * something else in that cell reports it and one that puts the same piece back does not.
     *
     * The weakest of the five, and worth saying so: the ticket asks for it so the system can
     * remember what was offered, and the backend already does that on its own when it pins the
     * pair. What this adds is the rate.
     */
    contentShown(kind: TodayTileKind, content: TodayContent): void {
      const offering = `${kind}:${content.id}`;
      if (shownContent.has(offering)) {
        return;
      }
      shownContent.add(offering);
      trackEvent(TodayEvent.contentShown, {
        ...common(),
        ...contentProperties(kind, content),
      });
    },

    /**
     * The welcome video reached its halfway mark, on the tile that offers it.
     *
     * The name is the whole distinction, and it is the reason this event exists at all: the same
     * act on any other piece produces `block_content_watched` from the backend, and that event
     * carries the piece but not where it was watched. Emitted only by the tasks tile, so a video
     * from a content cell can never arrive here.
     *
     * Marked like the two above: the tile announces every halfway mark, and the player's own memory
     * of having reported goes with the subtree on a re-fetch.
     */
    introWatched(contentId: string): void {
      if (watchedIntro.has(contentId)) {
        return;
      }
      watchedIntro.add(contentId);
      trackEvent(TodayEvent.introWatched, {
        ...common(),
        content_id: contentId,
      });
    },

    /**
     * An entry the user wrote on this page, and the one conversion the page earns without sending
     * anyone anywhere — which is why none of the other four can see it.
     *
     * Sent once the entry is stored rather than when the button is pressed: a failed save leaves
     * the draft on screen for another try, and counting that would count entries that do not
     * exist.
     *
     * Takes what was derived, not the entry: the note and the mood have no business in this module
     * even unread.
     */
    journalSaved(entry: {hasText: boolean; feelingsCount: number}): void {
      trackEvent(TodayEvent.journalSaved, {
        ...common(),
        ...journalProperties(entry),
      });
    },
  };
  /* eslint-enable @typescript-eslint/naming-convention */
}
